import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Lock,
} from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { ITEM_ICON, ITEM_LABEL, lockedUntilCopy, subjectAccent } from "./itemTypes";
import { splitUnitItems } from "@/lib/unitPlacement";
import { Mascot } from "./Mascot";
import { pathCheerLine, rememberPathCheer, unseenPathCheer, type PathCheer } from "./celebrations";

/**
 * The Learning Path — the trail view from the original AcuityLearn.
 *
 * A dashed spine down the left, a coloured banner for each top-level
 * container, and a pin for each nested one. The one rule that makes it read
 * as a path rather than a list of homework: only ONE thing on the page is
 * ever bright — the "up next" card. Everything finished recedes, everything
 * ahead sits flat.
 *
 * Depth picks the chrome (banner vs pin), not the words "unit" and "lesson".
 * Labels and untitled fallbacks come from the container's own tier.
 *
 * Later items stay locked until the previous required step is completed.
 * The server sets `locked` on each item (require_previous + progress +
 * submissions). This view only paints that — it does not decide the gate.
 * Guessing a URL still 409s on open.
 *
 * The server decides "up next", not this component — Today shows the same thing
 * and the two must never disagree.
 */

export type PathItem = {
  /** Above or below the unit's lessons, for items on the unit itself. */
  placement?: "before" | "after" | null;
  id: string;
  type: string;
  title: string | null;
  dueAt: string | null;
  pointsPossible: number | null;
  /** Null when nothing is marked yet — never render that as a zero. */
  pointsEarned: number | null;
  awaitingMarking?: boolean;
  status: "not_started" | "in_progress" | "completed";
  completionMode: "on_complete" | "on_submit" | "on_discussion" | "on_open" | "by_teacher" | "unknown";
  locked?: boolean;
  /** Next openable step that unblocks this item. Null when the item is not locked. */
  unlocksWith?: { activityId: string; title: string | null } | null;
};

export type PathContainer = {
  id: string;
  type: string;
  typeLabel: string;
  /** "Unit 3" / "3.2" — computed server-side from the container's tier and position. */
  label: string;
  title: string | null;
  position: number;
  /** The unit's own question. Lessons do not have one. */
  coreQuestion?: string | null;
  objectives?: string[];
  items: PathItem[];
  children: PathContainer[];
  done: number;
  total: number;
  percent: number;
  complete: boolean;
  childrenDone: number;
  childrenTotal: number;
};

export type PathData = {
  courseId: string;
  courseItems: PathItem[];
  containers: PathContainer[];
  upNext: { activityId: string; title: string | null; type: string } | null;
  totals: {
    activitiesTotal: number;
    activitiesDone: number;
    percent: number;
    containersTotal: number;
    containersDone: number;
  };
};

type CourseRow = {
  id: string;
  title: string;
  primaryTaxonomy: string | null;
  objectives?: string[];
};

/** Banner colours, cycled by top-level position. Exactly the original's four,
 *  in the original's order — LearnPath.tsx line 12. */
const BANNER_ACCENTS = ["#0AA0C4", "#1B3560", "#17677A", "#6B5CA5"];

function untitled(node: PathContainer) {
  return node.title || `Untitled ${node.typeLabel.toLowerCase()}`;
}

/**
 * The banner count is ACTIVITIES, whatever the unit contains. "0 of 4 lessons"
 * on a unit where the student is three activities into lesson 1 reads as zero
 * progress; "3 of 28 activities" is what they have actually done. Whole
 * lessons are mentioned only once at least one is finished, so the line never
 * leads with a zero.
 */
function childCountLabel(node: PathContainer) {
  const activities = `${node.done} of ${node.total} ${node.total === 1 ? "activity" : "activities"}`;
  if (node.childrenTotal === 0 || node.childrenDone === 0) return activities;
  const word = (node.children[0]?.typeLabel || "item").toLowerCase();
  const plural = node.childrenTotal === 1 ? word : `${word}s`;
  return `${activities} · ${node.childrenDone} of ${node.childrenTotal} ${plural} done`;
}

const points = (n: number) => `${n} ${n === 1 ? "point" : "points"}`;

function pathActivityId(activityId: string) {
  return `path-activity-${activityId}`;
}

/** Put the activity they just left in the middle of the path column. */
function scrollPathToActivity(activityId: string) {
  const el = document.getElementById(pathActivityId(activityId));
  const scroller = el?.closest(".student-main");
  if (!el || !(scroller instanceof HTMLElement)) return;
  const elRect = el.getBoundingClientRect();
  const rootRect = scroller.getBoundingClientRect();
  const elTop = elRect.top - rootRect.top + scroller.scrollTop;
  const centered = elTop - (scroller.clientHeight - elRect.height) / 2;
  const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  scroller.scrollTop = Math.max(0, Math.min(max, centered));
}

function labelFor(item: PathItem) {
  const bits: string[] = [];
  // Once a mark exists it replaces the bare point value — "25 / 30 points" is
  // the thing a student is looking for, and the total is still right there.
  if (item.pointsEarned != null && item.pointsPossible) {
    bits.push(`${item.pointsEarned} / ${points(item.pointsPossible)}`);
  } else if (item.awaitingMarking) {
    bits.push(`${points(item.pointsPossible ?? 0)} · being marked`);
  } else if (item.pointsPossible) {
    bits.push(points(item.pointsPossible));
  }
  if (item.dueAt) {
    const due = new Date(item.dueAt);
    const overdue = due.getTime() < Date.now();
    bits.push(`${overdue ? "was due" : "due"} ${due.toLocaleDateString()}`);
  }
  return bits.join(" · ");
}

export default function LearningPath() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToActivityId =
    (location.state as { returnToActivityId?: string } | null)?.returnToActivityId || "";
  const shell = useOutletContext<
    { path: PathData; course: { title: string; objectives?: string[] } } | undefined
  >();
  const embedded = Boolean(shell?.path);
  const [data, setData] = useState<PathData | null>(shell?.path ?? null);
  const [course, setCourse] = useState<CourseRow | null>(
    shell?.course
      ? {
          id: courseId || "",
          title: shell.course.title,
          primaryTaxonomy: null,
          objectives: shell.course.objectives,
        }
      : null,
  );
  const [error, setError] = useState("");
  const [cheer, setCheer] = useState<PathCheer | null>(null);

  const load = useCallback(async () => {
    if (!courseId || embedded) return;
    setError("");
    try {
      const [pathEnv, coursesEnv] = await Promise.all([
        api<{ success: boolean; data: PathData }>(
          `/api/student/courses/${encodeURIComponent(courseId)}/path`,
        ),
        api<{ success: boolean; data: CourseRow[] }>("/api/student/courses"),
      ]);
      setData(pathEnv.data);
      setCourse(coursesEnv.data.find((c) => c.id === courseId) || null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [courseId, embedded]);

  useEffect(() => {
    if (embedded && shell?.path) {
      setData(shell.path);
      return;
    }
    void load();
  }, [embedded, load, shell?.path]);

  useLayoutEffect(() => {
    if (!data || !returnToActivityId) return;
    scrollPathToActivity(returnToActivityId);
  }, [data, returnToActivityId]);

  useEffect(() => {
    if (embedded) return;
    if (!data || data.courseId !== courseId) {
      setCheer(null);
      return;
    }
    const next = unseenPathCheer(data.courseId, data.totals.percent);
    if (!next) return;
    setCheer(next);
    // Remember after this paint. Remembering inside the effect itself gets
    // wiped by the dev double-mount before the line is on screen.
    const id = data.courseId;
    const timer = window.setTimeout(() => rememberPathCheer(id, next), 0);
    return () => window.clearTimeout(timer);
  }, [embedded, courseId, data]);

  const accent = useMemo(() => subjectAccent(course?.title || ""), [course]);

  if (error) {
    return (
      <div className="student-page">
        <Link className="student-text-link" to="/student/learning">
          <ChevronLeft size={15} /> My Learning
        </Link>
        <div className="student-inline-error" role="alert" style={{ marginTop: 12 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return <PathOpeningSkeleton />;
  }

  const nothingPublished = data.totals.activitiesTotal === 0;
  const trail = (
    <>
      {nothingPublished ? (
        <div className="student-empty">
          <div>
            <Mascot pose="confused" size={92} />
            <strong>Nothing here yet</strong>
            <p>
              Your teacher has not added any work here yet. It will fill in as
              they do.
            </p>
          </div>
        </div>
      ) : (
        <div className="student-trail">
          <span className="student-trail__spine" aria-hidden />
          {data.containers.map((node, i) => (
            <TrailBanner
              key={node.id}
              node={node}
              accentIndex={i}
              upNextId={data.upNext?.activityId || null}
              onOpen={(activityId) => navigate(`/student/activity/${activityId}`)}
            />
          ))}
          {data.courseItems.map((item) => (
            <Waypoint
              key={item.id}
              item={item}
              isUpNext={data.upNext?.activityId === item.id}
              onOpen={() => {
                if (!item.locked) return navigate(`/student/activity/${item.id}`);
                if (item.unlocksWith?.activityId) {
                  navigate(`/student/activity/${item.unlocksWith.activityId}`);
                }
              }}
            />
          ))}
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div style={{ marginTop: 8 }}>{trail}</div>;
  }

  return (
    <div className="student-page" style={{ ["--student-accent" as string]: accent }}>
      <Link className="student-text-link" to="/student/learning">
        <ChevronLeft size={15} /> My Learning
      </Link>

      <div
        className="student-page-head student-page-head--withmascot"
        style={{ marginTop: 10 }}
      >
        <div style={{ minWidth: 0 }}>
          <span className="student-eyebrow">Learning path</span>
          <h1>{course?.title || "Your course"}</h1>
          {!nothingPublished && (
            <p>
              Work through one activity at a time.{" "}
              {data.totals.activitiesDone > 0 && (
                <strong style={{ color: "var(--s-green-d)" }}>
                  {data.totals.activitiesDone} of {data.totals.activitiesTotal} complete so far.
                </strong>
              )}
            </p>
          )}
        </div>
        <div className="student-path-cheer">
          <Mascot
            state={cheer || data.totals.percent === 100 ? "celebrate" : "idle"}
            size={72}
          />
          {cheer ? <p className="student-path-cheer__line">{pathCheerLine(cheer)}</p> : null}
        </div>
      </div>
      {trail}
    </div>
  );
}

/** A top-level container: the coloured banner, then its items and nested pins. */
function TrailBanner({
  node,
  accentIndex,
  upNextId,
  onOpen,
}: {
  node: PathContainer;
  accentIndex: number;
  upNextId: string | null;
  onOpen: (activityId: string) => void;
}) {
  const goals = node.objectives || [];
  return (
    <div>
      <div
        className="student-unitbar"
        style={{ ["--student-accent" as string]: BANNER_ACCENTS[accentIndex % BANNER_ACCENTS.length] }}
      >
        <div className="student-unitbar__label">{node.label}</div>
        <div className="student-unitbar__line">
          <div className="student-unitbar__title">{untitled(node)}</div>
          <div className="student-unitbar__count">{childCountLabel(node)}</div>
        </div>
        {node.coreQuestion ? <p className="student-unitbar__purpose">{node.coreQuestion}</p> : null}
        {goals.length > 0 ? (
          <>
            <p className="student-unitbar__kicker">You will be able to</p>
            <ol className="student-unitbar__objectives">
              {goals.map((line, i) => (
                <li key={`${node.id}-obj-${i}`}>{line}</li>
              ))}
            </ol>
          </>
        ) : null}
        <div className="student-unitbar__meter">
          <i style={{ width: `${node.percent}%` }} />
        </div>
      </div>

      {/* The unit's own items sit above or below its lessons, as the teacher
          placed them (unitPlacementOf). */}
      {splitUnitItems(node.items).before.map((item) => (
        <Waypoint
          key={item.id}
          item={item}
          isUpNext={upNextId === item.id}
          onOpen={() => {
            if (!item.locked) return onOpen(item.id);
            if (item.unlocksWith?.activityId) onOpen(item.unlocksWith.activityId);
          }}
        />
      ))}

      {node.children.map((child) => (
        <NestedPin key={child.id} node={child} upNextId={upNextId} onOpen={onOpen} />
      ))}

      {splitUnitItems(node.items).after.map((item) => (
        <Waypoint
          key={item.id}
          item={item}
          isUpNext={upNextId === item.id}
          onOpen={() => {
            if (!item.locked) return onOpen(item.id);
            if (item.unlocksWith?.activityId) onOpen(item.unlocksWith.activityId);
          }}
        />
      ))}
    </div>
  );
}

/**
 * A nested container (a lesson under a unit). The trail lists the activities
 * inside it — there is no separate lesson page to open. Collapsing them into
 * one "11 activities" card hid every step and looked like the path was empty.
 */
function NestedPin({
  node,
  upNextId,
  onOpen,
}: {
  node: PathContainer;
  upNextId: string | null;
  onOpen: (activityId: string) => void;
}) {
  return (
    <div className="student-lesson">
      <p className="student-lesson__label">
        <span>
          {node.typeLabel} {node.label}
        </span>
        {untitled(node)}
      </p>
      {node.items.map((item) => (
        <Waypoint
          key={item.id}
          item={item}
          isUpNext={upNextId === item.id}
          onOpen={() => {
            if (!item.locked) return onOpen(item.id);
            if (item.unlocksWith?.activityId) onOpen(item.unlocksWith.activityId);
          }}
        />
      ))}
    </div>
  );
}

/** A single activity sitting directly on the course or a container. */
function Waypoint({
  item,
  isUpNext,
  onOpen,
}: {
  item: PathItem;
  isUpNext: boolean;
  onOpen: () => void;
}) {
  const done = item.status === "completed";
  const locked = item.locked && !done;
  // Locked items stay locked even if they are also "up next" — never paint a
  // Start button on a gate the student cannot pass.
  const state = done ? "done" : locked ? "locked" : isUpNext ? "active" : "open";
  const Icon = ITEM_ICON[item.type] || BookOpen;
  const kind = ITEM_LABEL[item.type] || item.type;
  const lockCopy = lockedUntilCopy(item.unlocksWith?.title);
  const sub = locked ? [labelFor(item), lockCopy].filter(Boolean).join(" · ") : labelFor(item);
  const canOpenUnlock = Boolean(locked && item.unlocksWith?.activityId);
  const title = item.title || "Untitled activity";

  const lockedBody = (
    <>
      <WaypointText kind={kind} title={title} sub={sub} />
      <Lock size={14} style={{ color: "var(--s-locked-ink)", flex: "none" }} />
    </>
  );

  return (
    <div className="student-node" id={pathActivityId(item.id)}>
      <span className={`student-pin student-pin--${state}`} aria-hidden>
        <Icon size={state === "active" ? 16 : 14} />
      </span>

      {state === "active" ? (
        <div className="student-upnext">
          <div style={{ minWidth: 0 }}>
            <span className="student-upnext__badge">{kind}</span>
            <h3>{title}</h3>
            {sub && <div className="student-upnext__sub">{sub}</div>}
          </div>
          <button type="button" className="student-btn" onClick={onOpen}>
            {item.status === "in_progress" ? "Continue" : "Start"}
          </button>
        </div>
      ) : locked ? (
        canOpenUnlock ? (
          <button
            type="button"
            className="student-waypoint student-waypoint--locked student-waypoint--locked-open"
            onClick={onOpen}
          >
            {lockedBody}
          </button>
        ) : (
          <div className="student-waypoint student-waypoint--locked">{lockedBody}</div>
        )
      ) : (
        <button
          type="button"
          className={`student-waypoint${done ? " student-waypoint--done" : ""}`}
          onClick={onOpen}
        >
          <WaypointText kind={kind} title={title} sub={sub} />
          {done ? (
            <span className="student-waypoint__stat">Complete</span>
          ) : (
            <ChevronRight size={15} style={{ color: "var(--s-muted)", flex: "none" }} />
          )}
        </button>
      )}
    </div>
  );
}

function WaypointText({ kind, title, sub }: { kind: string; title: string; sub?: string }) {
  return (
    <span>
      <span className="student-waypoint__kind">{kind}</span>
      <strong>{title}</strong>
      {sub ? <span className="student-waypoint__sub">{sub}</span> : null}
    </span>
  );
}

function PathOpeningSkeleton() {
  return (
    <div className="student-page" aria-busy="true" aria-live="polite">
      <span className="student-vh">Loading your path</span>
      <div className="student-ghost student-ghost--link" />
      <div className="student-page-head student-page-head--withmascot" style={{ marginTop: 10 }}>
        <div className="student-ghost-head student-ghost-head--path">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--title" />
          <div className="student-ghost student-ghost--line student-ghost--w60" style={{ marginTop: 10 }} />
        </div>
        <div className="student-ghost student-ghost--mascot" aria-hidden />
      </div>
      <PathTrailSkeleton />
    </div>
  );
}

/** Trail only — CourseHome paints the class chrome, then this while the path loads. */
export function PathTrailSkeleton() {
  return (
    <div className="student-trail student-trail--ghost" style={{ marginTop: 8 }}>
      <span className="student-trail__spine" aria-hidden />
      <GhostBanner />
      <GhostWaypoint next />
      <GhostWaypoint />
      <div className="student-lesson">
        <div className="student-ghost student-ghost--lesson" />
        <GhostWaypoint />
        <GhostWaypoint />
      </div>
      <GhostBanner />
      <GhostWaypoint />
      <GhostWaypoint />
    </div>
  );
}

function GhostBanner() {
  return (
    <div className="student-unitbar student-ghost-unitbar">
      <div className="student-ghost student-ghost--meta" />
      <div className="student-unitbar__line">
        <div className="student-ghost student-ghost--line student-ghost--w70" />
        <div className="student-ghost student-ghost--meta" />
      </div>
      <div className="student-ghost student-ghost--meter" />
    </div>
  );
}

function GhostWaypoint({ next }: { next?: boolean }) {
  return (
    <div className="student-node">
      <span className={`student-ghost student-ghost--pin${next ? " student-ghost--pin-next" : ""}`} aria-hidden />
      <div className={`student-ghost-waypoint${next ? " student-ghost-waypoint--next" : ""}`}>
        {next ? <div className="student-ghost student-ghost--chip" /> : null}
        <div className={`student-ghost student-ghost--line${next ? "" : " student-ghost--w80"}`} />
        <div className="student-ghost student-ghost--meta" />
      </div>
    </div>
  );
}
